import React, { useState } from 'react';
import AiSummaryItem from './AiSummaryItem';
import AiSummaryInitialState from './AiSummaryInitialState';
import AiSummaryLoadingSkeleton from './AiSummaryLoadingSkeleton';
import AiSummaryPeriodSelector from './AiSummaryPeriodSelector';

/**
 * AiInboxSummaryBanner Component
 * Master briefing card pinned above the operational tabs (Spec §3, §5).
 * Coordinates period selection, initial ungenerated state, loading skeletons,
 * individual statement deep links, and expanded modal trigger.
 */
export default function AiInboxSummaryBanner({
    summaryData,
    selectedPeriod,
    selectedProject = 'All',
    onPeriodChange,
    onCustomRangeSelect,
    onRefresh,
    onGenerate,
    isLoading = false,
    onOpenFullModal,
    onItemActionClick
}) {
    const [dismissed, setDismissed] = useState(false);

    // If user dismissed the banner, provide a sleek pill to restore it
    if (dismissed) {
        return (
            <div className="flex items-center justify-between px-4 py-2 bg-blue-50/50 dark:bg-slate-800/60 border border-blue-200/50 dark:border-blue-900/40 rounded-xl text-xs text-slate-600 dark:text-slate-300 mb-4 transition-all">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-[#006591] dark:text-[#00AAF1]">auto_awesome</span>
                    <span>AI Inbox Summary is currently hidden.</span>
                </div>
                <button
                    onClick={() => setDismissed(false)}
                    className="text-[#03488A] dark:text-[#00AAF1] hover:underline font-semibold cursor-pointer"
                >
                    Show Summary
                </button>
            </div>
        );
    }

    const items = summaryData?.items || [];
    const hasItems = items.length > 0;
    const activeProjectScope = summaryData?.projectScope || (selectedProject !== 'All' ? selectedProject : 'All Projects');

    const previewLimit = 4;
    const previewItems = items.slice(0, previewLimit);
    const hiddenCount = items.length - previewLimit;

    return (
        <div
            id="inbox-summary-banner"
            className="relative bg-gradient-to-r from-blue-50/70 via-white to-sky-50/60 dark:from-slate-800/80 dark:via-slate-850 dark:to-blue-950/30 border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-5 shadow-xs transition-all duration-300"
        >
            {/* Summary Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-blue-100/70 dark:border-blue-900/40">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-blue-600/10 dark:bg-blue-500/20 text-[#03488A] dark:text-[#00AAF1] flex items-center justify-center">
                        <span className="material-symbols-outlined text-lg">auto_awesome</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-base font-bold text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                            Inbox Summary —
                        </h2>
                        <AiSummaryPeriodSelector
                            selectedPeriod={selectedPeriod}
                            onPeriodChange={onPeriodChange}
                            onCustomRangeSelect={onCustomRangeSelect}
                            disabled={isLoading}
                        />
                        {/* Scope Indicator Pill */}
                        {selectedProject && selectedProject !== 'All' ? (
                            <span
                                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100/80 dark:bg-blue-900/50 text-[#03488A] dark:text-[#00AAF1] border border-blue-200/80 dark:border-blue-800/60 max-w-[200px] truncate shadow-2xs"
                                title={`Filtered by project: ${selectedProject}`}
                            >
                                <span className="material-symbols-outlined text-xs">folder_open</span>
                                <span className="truncate">{selectedProject}</span>
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200/60 dark:border-slate-700/60">
                                <span className="material-symbols-outlined text-xs">domain</span>
                                All Projects
                            </span>
                        )}
                    </div>

                    <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs text-blue-500">schedule</span>
                        Updated {(() => {
                            if (!summaryData?.updatedAt) return 'just now';
                            const d = new Date(summaryData.updatedAt);
                            return !isNaN(d.getTime()) ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : String(summaryData.updatedAt);
                        })()} • AI Assistant Briefing
                    </span>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={onRefresh}
                        disabled={isLoading}
                        className={`p-1.5 text-slate-400 hover:text-[#03488A] dark:hover:text-[#00AAF1] hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all cursor-pointer ${
                            isLoading ? 'animate-spin text-[#03488A]' : ''
                        }`}
                        title="Refresh Summary"
                    >
                        <span className="material-symbols-outlined text-base">refresh</span>
                    </button>
                    <button
                        onClick={onOpenFullModal}
                        disabled={!hasItems || isLoading}
                        className="p-1.5 text-slate-400 hover:text-[#03488A] dark:hover:text-[#00AAF1] hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all cursor-pointer disabled:opacity-40"
                        title="Open Full Summary Modal"
                    >
                        <span className="material-symbols-outlined text-base">open_in_new</span>
                    </button>
                    <button
                        onClick={() => setDismissed(true)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all ml-1 cursor-pointer"
                        title="Dismiss Briefing"
                    >
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                </div>
            </div>

            {/* Main Content Area (Compact Scannable Highlights - Spec §5) */}
            {isLoading ? (
                <AiSummaryLoadingSkeleton />
            ) : !hasItems ? (
                <AiSummaryInitialState
                    onGenerate={onGenerate || onRefresh}
                    isGenerating={isLoading}
                    selectedPeriod={selectedPeriod}
                    selectedProject={selectedProject}
                />
            ) : (
                <div className="py-3.5 space-y-2.5">
                    {previewItems.map((item) => (
                        <AiSummaryItem
                            key={item.id}
                            item={item}
                            onItemActionClick={onItemActionClick}
                        />
                    ))}
                </div>
            )}

            {/* Summary Footer Actions (Only when items exist and not loading) */}
            {hasItems && !isLoading && (
                <div className="flex items-center justify-between pt-3 border-t border-blue-100/70 dark:border-blue-900/40">
                    <div className="flex items-center gap-2.5">
                        <button
                            onClick={onOpenFullModal}
                            className="h-8 px-3.5 text-xs font-bold leading-none text-white rounded-xl shadow-2xs hover:opacity-90 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer"
                            style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                        >
                            <span className="material-symbols-outlined text-sm">description</span> View Full Summary
                        </button>
                        {hiddenCount > 0 && (
                            <button
                                onClick={onOpenFullModal}
                                className="text-xs text-blue-700 dark:text-[#00AAF1] hover:underline font-semibold cursor-pointer"
                            >
                                + {hiddenCount} more {hiddenCount === 1 ? 'update' : 'updates'} in Full Summary
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onRefresh}
                        className="h-8 px-3.5 text-xs font-bold leading-none text-[#03488A] dark:text-[#00AAF1] hover:text-blue-900 dark:hover:text-blue-300 flex items-center gap-1.5 rounded-xl border border-blue-200/60 dark:border-blue-800/60 hover:bg-white/80 dark:hover:bg-slate-800 transition-all cursor-pointer"
                    >
                        <span className="material-symbols-outlined text-sm">autorenew</span>
                        Refresh Summary
                    </button>
                </div>
            )}
        </div>
    );
}
